import React, { useEffect, useRef, useState } from "react";
import { ChevronsRight, Camera, ArrowRight } from "lucide-react";
import { BEFORE_AFTER, TEAM_CAPTION } from "@/lib/before-after";

const CleaningDifference = ({ onOrderClick }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [full, setFull] = useState(() => new Set([0]));

  // Load the 900px image only when a card scrolls near the viewport; others show the 15KB thumb.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || !("IntersectionObserver" in window)) { setFull(new Set(BEFORE_AFTER.map((_, i) => i))); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) setFull((s) => (s.has(+en.target.dataset.idx) ? s : new Set(s).add(+en.target.dataset.idx)));
      });
    }, { root: el, rootMargin: "0px 40% 0px 40%", threshold: 0.01 });
    Array.from(el.children).forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !el.firstChild) return;
    const w = el.firstChild.getBoundingClientRect().width + 12;
    setActive(Math.min(BEFORE_AFTER.length - 1, Math.round(el.scrollLeft / w)));
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el || !el.children[i]) return;
    el.children[i].scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  return (
    <section className="mt-5" data-testid="cleaning-difference">
      <div className="px-4 mb-4 text-center">
        <div className="inline-block text-[11px] tracking-[0.18em] font-semibold text-emerald-800 bg-emerald-100/70 rounded-full px-3 py-1 mb-2 uppercase">Before / After</div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0a1a14] leading-tight">See the Cleaning Difference</h2>
        <p className="text-xs text-slate-500 mt-1.5 inline-flex items-center gap-1" data-testid="ba-swipe-hint">
          Swipe to explore <ChevronsRight size={14} className="animate-pulse" />
        </p>
      </div>

      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pl-4 pr-4 pb-2 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollPaddingLeft: 16 }}
        data-testid="ba-track"
      >
        {BEFORE_AFTER.map((item, i) => (
          <div
            key={item.slug}
            data-idx={i}
            className="card-soft shrink-0 w-[82%] max-w-[360px] snap-start overflow-hidden"
            data-testid={`ba-card-${item.slug}`}
          >
            <img
              src={full.has(i) ? item.src : item.thumb}
              alt={`${item.title} — before and after cleaning`}
              width="900"
              height="900"
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "auto" : "low"}
              decoding="async"
              draggable="false"
              data-res={full.has(i) ? "full" : "thumb"}
              className="w-full aspect-square object-contain bg-emerald-50/40 select-none"
            />
            <div className="px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="font-extrabold text-emerald-900 text-[14px] leading-tight">{item.title}</div>
                <button
                  type="button"
                  onClick={onOrderClick}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-[11px] font-bold px-3 py-1.5 transition-[background-color,transform]"
                  data-testid={`ba-order-btn-${item.slug}`}
                >
                  Order Now <ArrowRight size={12} />
                </button>
              </div>
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500" data-testid="ba-team-caption">
                <Camera size={11} className="text-emerald-700" />
                {TEAM_CAPTION}{item.capturedOn ? ` · ${item.capturedOn}` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-1.5 mt-2" data-testid="ba-dots">
        {BEFORE_AFTER.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-5 bg-emerald-600" : "w-1.5 bg-emerald-200"}`}
            data-testid={`ba-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
};

export default CleaningDifference;
